interface Entry {
	count: number;
	settle: () => void;
	marker: Promise<void>;
}

const pending = new Map<string, Entry>();

/**
 * The same article's path alone is not a safe key: it repeats across catalogs, and a component can
 * move from one catalog's `first.md` to another's without remounting (see the `articleKey` comment
 * next to `useSaveArticle` in ArticleEditRenderer.tsx, which needs the same disambiguation for the
 * same reason). Catalog name and path together are exactly what `ApiUrlCreator` is already unique on.
 */
const key = (catalogName: string, articlePath: string): string => `${catalogName}/${articlePath}`;

/**
 * Marks a write of this catalog's article at `articlePath` as in flight, until the returned function
 * is called. Props writes count too: a read answers with the content and the props alike.
 *
 * Reference-counted, like `AppBusy.hold`: a second write for the same article while the first is
 * still in flight (the debounce firing again before the earlier write has landed, or the title write
 * queued behind the content one) must not let the first write's release clear the mark out from under
 * the second one. The article is kept "in flight" for a concurrent read until every
 * `beginArticleWrite` call for it has been released.
 *
 * Call as early as the edit itself, before the write it will become is even dispatched — that is the
 * window `awaitPendingArticleWrite` closes for a concurrent read; see there for why it has to be
 * this early.
 */
export const beginArticleWrite = (catalogName: string, articlePath: string): (() => void) => {
	const k = key(catalogName, articlePath);
	let entry = pending.get(k);
	if (!entry) {
		let settle: () => void;
		const marker = new Promise<void>((resolve) => {
			settle = resolve;
		});
		entry = { count: 0, marker, settle: settle! };
		pending.set(k, entry);
	}
	entry.count++;
	const current = entry;
	let released = false;

	return () => {
		if (released) throw new Error(`pendingArticleWrite: release("${k}") called more than once`);
		released = true;
		current.count--;
		if (current.count === 0) {
			pending.delete(k);
			current.settle();
		}
	};
};

/**
 * Runs a write nobody awaits and keeps the article pending until it lands or fails. A debounced save takes
 * `beginArticleWrite` at the edit instead.
 */
export const trackArticleWrite = async <T>(
	catalogName: string,
	articlePath: string,
	write: () => Promise<T>,
): Promise<T> => {
	const release = beginArticleWrite(catalogName, articlePath);
	try {
		return await write();
	} finally {
		release();
	}
};

/**
 * Waits out a write already in flight for this article, if there is one.
 *
 * `parseContent`'s cache is per-article and is only refreshed once a write's own re-parse lands; a
 * read that runs between the write starting and that landing sees the pre-write cache and answers
 * with the content the write was meant to replace. The window is real and starts earlier than the
 * write command itself: an editor's debounced save is armed on the first keystroke (`AppBusy.hold`
 * fires right there, before the request is even built), and a client-side route back to the same
 * article can re-render, and re-read, well before that request reaches the command that resolves the
 * article it is writing to and starts tracking there would.
 */
export const awaitPendingArticleWrite = async (catalogName: string, articlePath: string): Promise<void> => {
	await pending.get(key(catalogName, articlePath))?.marker;
};
